import _ from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import cookie from 'react-cookies'
import variant from '../flags_variants/variant'
import { validateAccessRole } from '../helpers/userRole'
import { canViewUsersApp } from './capabilities/userAppCapabilities'
import { canViewReports } from './capabilities/reportCapabilities'
import { canManagePromotions } from './capabilities/promoCapabilities'
import { GlobalContext } from './GlobalContext'

const APP_NAME = process.env.REACT_APP_BUSINESS_NAME.toLowerCase()

const FlagProvider = ({ children }) => {
  const [isReady, setIsReady] = useState(false)

  const [data, setData] = useState({})
  const [shouldViewCoreInfo, setShouldViewCoreInfo] = useState(null)
  const [shouldViewTransactionInfo, setShouldViewTransactionInfo] = useState(null)
  const [shouldCreateOrder, setShouldCreateOrder] = useState(false)
  const [hasTenantReport, setTenantReport] = useState(false)
  const [redirectPath, setRedirectPath] = useState(false)
  const [hasAppEntrega, setHasAppEntrega] = useState(false)
  const [useAve, setUseAve] = useState(false)
  const [userRole, setUserRole] = useState(null)

  const syncUserRole = useCallback(() => {
    setUserRole(cookie.load('user_role') || {})
  }, [])

  const clearUserRole = useCallback(() => {
    setUserRole(null)
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      const variants = await JSON.parse(JSON.stringify(variant))
      const lookup = _.keyBy(variants, (pr) => pr.clientName)
      const config = lookup[APP_NAME]

      setData(config)
      setShouldCreateOrder(config.shouldCreateOrder)
      setShouldViewCoreInfo(config.shouldViewCoreInfo)
      setShouldViewTransactionInfo(config.shouldViewTransactionInfo)
      setRedirectPath(config.redirectPath)
      setTenantReport(config.hasTenantReport)
      setHasAppEntrega(config.hasAppEntrega)
      setUseAve(config.useAve)

      const role = cookie.load('user_role') || {}
      setUserRole(role)

      setIsReady(true)
    }

    fetchData()
  }, [])

  useEffect(() => {
    window.__updateUserRole = syncUserRole
    return () => {
      if (window.__updateUserRole === syncUserRole) {
        delete window.__updateUserRole
      }
    }
  }, [syncUserRole])

  const contextValue = useMemo(() => {
    if (!isReady) {
      return {
        variants: {},
        userRole: null,
        supervisorRole: true,
        shouldCreateOrder: false,
        shouldViewCoreInfo: null,
        shouldViewTransactionInfo: null,
        redirectPath: false,
        canMakePromoRole: false,
        hasAppEntrega: false,
        hasTenantReport: false,
        useAve: false,
        configPermission: false,
        canViewReport: false,
        isReady: false,
        syncUserRole,
        clearUserRole
      }
    }

    const canViewCore = userRole ? validateAccessRole(userRole.can_view_core) : false

    const getRedirectPath = () => 'inicio'

    const supervisorRole = true
    const shouldViewCoreInfoByRole =
      shouldViewCoreInfo && supervisorRole && Boolean(canViewCore?.can_read)

    const canMakePromoRole = canManagePromotions()
    const redirectPathByRole = getRedirectPath()

    return {
      variants: data,
      userRole,
      supervisorRole,
      shouldCreateOrder,
      shouldViewCoreInfo: shouldViewCoreInfoByRole,
      shouldViewTransactionInfo,
      redirectPath: redirectPathByRole,
      canMakePromoRole,
      hasAppEntrega,
      hasTenantReport,
      useAve,
      configPermission: canViewUsersApp(),
      canViewReport: canViewReports(),
      isReady,
      syncUserRole,
      clearUserRole
    }
  }, [
    data,
    userRole,
    shouldCreateOrder,
    shouldViewCoreInfo,
    shouldViewTransactionInfo,
    hasAppEntrega,
    hasTenantReport,
    useAve,
    isReady,
    syncUserRole,
    clearUserRole
  ])

  return (
    <GlobalContext.Provider value={contextValue}>
      {children}
    </GlobalContext.Provider>
  )
}

export default FlagProvider
